import type { NavigationEvents } from '../adapters/next-router-adapter'
import { getSessionItem, setSessionItem } from '../storage/safe-storage'

const SS_KEY = 'app:nav:isPageReloaded'
const RELOADED = 'true'
const NOT_RELOADED = 'false'

/**
 * Tracks whether the current page has been reloaded
 * Provides abstraction for page reload detection across navigation events
 */
export class PageReloadTracker {
	private inited = false
	private offNavigation?: () => void

	constructor(private navigation?: NavigationEvents) {}

	/**
	 * Initialize the tracker with event listeners
	 * Safe to call multiple times - will only initialize once
	 */
	init(): void {
		if (this.inited || typeof window === 'undefined') return

		this.inited = true

		// Listen for page unload (reload/close)
		window.addEventListener('beforeunload', PageReloadTracker.onBeforeUnload)

		// Listen for navigation changes (SPA routing)
		if (this.navigation) {
			this.offNavigation = this.navigation.onBeforeHistoryChange(() => {
				setSessionItem(SS_KEY, NOT_RELOADED)
			})
		}
	}

	/**
	 * Clean up event listeners
	 */
	destroy(): void {
		if (!this.inited || typeof window === 'undefined') return

		this.inited = false

		window.removeEventListener('beforeunload', PageReloadTracker.onBeforeUnload)
		this.offNavigation?.()
	}

	/**
	 * Get current reload status
	 * @returns true if page was reloaded, false if navigated via SPA
	 */
	getStatus(): boolean {
		return getSessionItem(SS_KEY) === RELOADED
	}

	/**
	 * Reset reload status (useful for testing)
	 */
	reset(): void {
		setSessionItem(SS_KEY, NOT_RELOADED)
	}

	private static onBeforeUnload(): void {
		setSessionItem(SS_KEY, RELOADED)
	}
}
